import React from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../blocks/PageHeader";
import { Link, makeStyles } from "@fluentui/react-components";
import { themeTokens } from "../utils/Theming";

const useStyles = makeStyles({
  container: {
    padding: "20px",
    height: "calc(100vh - 77px)",
    overflowY: "auto",
  },
  heading: {
    lineHeight: "normal",
    fontSize: themeTokens.fontSizeBase400,
  },
  subHeading: {
    fontSize: themeTokens.fontSizeBase300,
  },
  paragraph: {
    fontSize: themeTokens.fontSizeBase200,
  },
  listItem: {
    fontSize: themeTokens.fontSizeBase200,
  },
});

const LearnMore = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <PageHeader onBack={handleBack} />
      <div className={styles.container}>
        <h1 className={styles.heading}>Supercharge your recruitment workflow right from Outlook</h1>
        <p className={styles.paragraph}>
          Ever wish you had all your crucial recruitment data at your fingertips while managing your emails? The{" "}
          <strong>OTYS Go! Outlook add in</strong> makes that a reality. Seamlessly integrated into your Outlook inbox,
          this powerful tool brings the essential information from OTYS Go! directly to you, helping you work smarter,
          faster, and more effectively.
        </p>
        <h2 className={styles.subHeading}>Understand who you're talking to, instantly</h2>
        <p className={styles.paragraph}>
          No more switching between applications or searching for details. When you open an email, our smart add in
          automatically detects if you're communicating with a candidate, relation, or contact person. It then instantly
          displays their relevant profile information from OTYS Go! right alongside your email. You'll have the context
          you need to engage confidently and professionally, every time.
        </p>
        <h2 className={styles.subHeading}>Take action without leaving your inbox</h2>
        <p className={styles.paragraph}>
          Why interrupt your flow? With the OTYS Go! Outlook Add in, you can perform critical recruitment tasks without
          ever leaving Outlook.
        </p>
        <p className={styles.paragraph}>Here’s what you can do:</p>
        <ul>
          <li className={styles.listItem}>
            <strong>View general information:</strong> See the most important information directly.
          </li>
          <li className={styles.listItem}>
            <strong>View linked vacancies:</strong> See all job openings associated with the person you're emailing.
          </li>
          <li className={styles.listItem}>
            <strong>Add notes & documents:</strong> Quickly add important notes or upload documents (for example parsing
            a CV) directly to their OTYS Go! dossier.
          </li>
          <li className={styles.listItem}>
            <strong>Link emails to:</strong> Easily link emails to the correct entity in OTYS for a complete record.
          </li>
          <li className={styles.listItem}>
            <strong>Create new candidates, contact persons or relations:</strong> Is there no email recognized? No
            problem. Just create a new candidate profile on the fly? Do it instantly from within Outlook.
          </li>
        </ul>
        <h2 className={styles.subHeading}>Fully synchronized for seamless operations</h2>
        <p className={styles.paragraph}>
          The OTYS Go! Outlook add in is always in sync with your OTYS Go! account, ensuring that all actions you take
          in Outlook are immediately reflected in your recruitment database. This real-time synchronization means you
          always have the most up-to-date information, no matter where you're working.
        </p>
        <p className={styles.paragraph}>
          Need more information? You can always open the candidate, contact person, or relation directly in OTYS Go! in
          a <strong>new tab.</strong>
        </p>
        <h2 className={styles.subHeading}>Need help?</h2>
        <p className={styles.paragraph}>
          Our support team is here for you. If you have any questions or require assistance with the add in, please
          don't hesitate to contact{" "}
          <Link href="https://www.otys.com/en/support/" target="_blank" rel="noopener noreferrer">
            support.
          </Link>
        </p>
      </div>
    </>
  );
};

export default LearnMore;
